import { DOCUMENT } from '@angular/common';
import { Component, ElementRef, inject, signal } from '@angular/core';
import { NgbToastModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslatePipe } from '@ngx-translate/core';

import { Toast, ToastService } from '../services/toast.service';

@Component({
  selector: 'app-toasts-container',
  imports: [NgbToastModule, TranslatePipe],
  template: `
    <div class="container">
      @for (toast of toastService.toasts(); track toast.text) {
        <ngb-toast
          [class]="{
            'text-bg-success': toast.type === 'success',
            'text-bg-danger': toast.type === 'danger',
            'text-bg-warning': toast.type === 'warning',
            'text-bg-info': toast.type === 'info',
          }"
          [autohide]="!isPaused(toast)"
          [delay]="toast.delay"
          (hidden)="toastService.remove(toast)"
          (mouseenter)="hoveredText.set(toast.text)"
          (mouseleave)="hoveredText.set(null)"
          (focusin)="focusedText.set(toast.text)"
          (focusout)="focusedText.set(null)"
          (keydown.escape)="close(toast, $event)"
        >
          <div class="d-flex align-items-start gap-2">
            <div class="flex-grow-1 text-break">
              <span
                class="bi me-2"
                [class]="{
                  'bi-x-circle': toast.type === 'danger',
                  'bi-check-circle': toast.type === 'success',
                  'bi-exclamation-triangle': toast.type === 'warning',
                  'bi-info-circle': toast.type === 'info',
                }"
              ></span>
              @if (toast.html) {
                <span [innerHTML]="toast.text"></span>
              } @else {
                <span>{{ toast.text }}</span>
              }
            </div>
            <button
              type="button"
              class="toast-close btn btn-link text-reset border-0 p-0 lh-1 fs-4 bi bi-x"
              [attr.aria-label]="'WEB_ACTIONS_CLOSE' | translate"
              (click)="close(toast, $event)"
            ></button>
          </div>
        </ngb-toast>
      }
    </div>
  `,
  styles: `
    .toast-close {
      min-width: 1.5rem;
      min-height: 1.5rem;

      &:focus-visible {
        outline-color: currentColor;
      }
    }
  `,
  host: {
    class: 'toast-container position-fixed p-3 mx-auto start-0 end-0 top-0',
    style: 'z-index: 1200',
    '(focusin)': 'rememberFocusOrigin($event)',
    '(document:visibilitychange)': 'onVisibilityChange()',
  },
})
export class ToastsContainerComponent {
  toastService = inject(ToastService);
  document = inject(DOCUMENT);
  element = inject<ElementRef<HTMLElement>>(ElementRef);

  hoveredText = signal<Nullable<string>>(null);
  focusedText = signal<Nullable<string>>(null);
  pageHidden = signal(this.document.hidden);

  focusOrigin: Nullable<HTMLElement> = null;

  isPaused = (toast: Toast) =>
    this.pageHidden() ||
    toast.text === this.hoveredText() ||
    toast.text === this.focusedText();

  onVisibilityChange = () => {
    this.pageHidden.set(this.document.hidden);
  };

  rememberFocusOrigin = (event: FocusEvent) => {
    const origin = event.relatedTarget;

    if (origin instanceof Node && this.element.nativeElement.contains(origin)) {
      return;
    }

    this.focusOrigin = origin instanceof HTMLElement ? origin : null;
  };

  close = (toast: Toast, event: Event) => {
    const toastElement = (event.currentTarget as HTMLElement).closest(
      'ngb-toast'
    );
    const closedWithKeyboard =
      event instanceof KeyboardEvent ||
      (event instanceof MouseEvent && event.detail === 0);

    if (
      closedWithKeyboard &&
      toastElement?.contains(this.document.activeElement)
    ) {
      this.moveFocusAwayFrom(toastElement);
    }

    this.toastService.remove(toast);
  };

  moveFocusAwayFrom = (toastElement: Element) => {
    const toastElements = Array.from(
      this.element.nativeElement.querySelectorAll('ngb-toast')
    );
    const index = toastElements.indexOf(toastElement);
    const neighbour = toastElements[index + 1] ?? toastElements[index - 1];
    const neighbourCloseButton = neighbour?.querySelector('button');

    if (neighbourCloseButton) {
      neighbourCloseButton.focus();
    } else if (this.focusOrigin?.isConnected) {
      this.focusOrigin.focus();
      this.focusOrigin = null;
    }
  };
}
